/**
 * Directly adapted from PlayCanvas GSplat WorkBuffer GLSL chunks at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

const gsplatSplatVS = /* glsl */ `
struct Splat {
    uint index;
    ivec2 uv;
};

Splat splat;

void setSplat(uint index) {
    splat.index = index;
    splat.uv = ivec2(index % splatTextureSize, index / splatTextureSize);
}
`

const gsplatFormatVS = /* glsl */ `
uniform uint splatTextureSize;
`

const gsplatHelpersVS = /* glsl */ `
void gsplatMakeSpherical(inout vec3 scale, float size) {
    scale = vec3(size);
}

float gsplatGetSizeFromScale(vec3 scale) {
    return sqrt((scale.x * scale.x + scale.y * scale.y + scale.z * scale.z) / 3.0);
}
`

const gsplatStructsVS = /* glsl */ `
#include "gsplatSplatVS"

struct SplatSource {
    uint order;
    vec2 cornerUV;
};

struct SplatCenter {
    vec3 view;
    vec4 proj;
    mat4 modelView;
    float projMat00;
    vec3 modelCenterOriginal;
    vec3 modelCenterModified;
    #ifdef GSPLAT_FISHEYE
        float fisheyeSinTK;
        float fisheyeCosTK;
        float fisheyeRxy;
    #endif
};

struct SplatCorner {
    vec3 offset;
    vec2 uv;
    #ifdef GSPLAT_AA
        float aaFactor;
    #endif
    vec2 v;
    float dlen;
};
`

const gsplatCenterVS = /* glsl */ `
uniform mat4 matrix_model;
uniform mat4 matrix_view;
#ifndef GSPLAT_CENTER_NOPROJ
    uniform vec4 camera_params;
    uniform mat4 matrix_projection;
    #ifdef GSPLAT_FISHEYE
        uniform float fisheye_k;
        uniform float fisheye_inv_k;
        uniform float fisheye_projMat00;
        uniform float fisheye_projMat11;
    #endif
#endif

bool initCenter(vec3 modelCenter, inout SplatCenter center) {
    mat4 modelView = matrix_view * matrix_model;
    vec4 centerView = modelView * vec4(modelCenter, 1.0);

    #ifndef GSPLAT_CENTER_NOPROJ
        #ifdef GSPLAT_FISHEYE
            vec3 v = centerView.xyz;
            float r_xy = length(v.xy);
            float neg_z = -v.z;
            float theta = atan(r_xy, neg_z);
            float maxTheta = min(fisheye_k * 1.5707963, 3.13);
            if (theta > maxTheta - 0.01 || dot(v, v) < 0.0001) return false;
            float tk = theta * fisheye_inv_k;
            float sin_tk = sin(tk);
            float cos_tk = cos(tk);
            float g_theta = fisheye_k * sin_tk / cos_tk;
            float fisheye_s = (r_xy > 1e-4) ? g_theta / r_xy : (neg_z > 0.0 ? 1.0 / neg_z : 0.0);
            vec2 ndc = vec2(fisheye_projMat00 * fisheye_s * v.x, fisheye_projMat11 * fisheye_s * v.y);
            float near = camera_params.z;
            float far = camera_params.y;
            float linearDepth = neg_z;
            #ifdef WEBGPU
                float depthNdc = clamp((linearDepth - near) / (far - near), 0.0, 1.0);
            #else
                float depthNdc = clamp(2.0 * (linearDepth - near) / (far - near) - 1.0, -1.0, 1.0);
            #endif
            center.proj = vec4(ndc, depthNdc, 1.0);
            center.projMat00 = fisheye_projMat00;
            center.fisheyeSinTK = sin_tk;
            center.fisheyeCosTK = cos_tk;
            center.fisheyeRxy = r_xy;
        #else
            if (camera_params.w != 1.0 && centerView.z > 0.0) return false;
            vec4 centerProj = matrix_projection * centerView;
            #ifdef WEBGPU
                centerProj.z = clamp(centerProj.z, 0.0, abs(centerProj.w));
            #else
                centerProj.z = clamp(centerProj.z, -abs(centerProj.w), abs(centerProj.w));
            #endif
            center.proj = centerProj;
            center.projMat00 = matrix_projection[0][0];
        #endif
    #endif

    center.view = centerView.xyz / centerView.w;
    center.modelView = modelView;
    return true;
}
`

const gsplatEvalSHVS = /* glsl */ `
#if SH_BANDS == 1
    #define SH_COEFFS 3
#elif SH_BANDS == 2
    #define SH_COEFFS 8
#elif SH_BANDS == 3
    #define SH_COEFFS 15
#else
    #define SH_COEFFS 0
#endif

#if SH_BANDS > 0
const float SH_C1 = 0.4886025119029199;
#if SH_BANDS > 1
    const float SH_C2_0 = 1.0925484305920792;
    const float SH_C2_1 = -1.0925484305920792;
    const float SH_C2_2 = 0.31539156525252005;
    const float SH_C2_3 = -1.0925484305920792;
    const float SH_C2_4 = 0.5462742152960396;
#endif
#if SH_BANDS > 2
    const float SH_C3_0 = -0.5900435899266435;
    const float SH_C3_1 = 2.890611442640554;
    const float SH_C3_2 = -0.4570457994644658;
    const float SH_C3_3 = 0.3731763325901154;
    const float SH_C3_4 = -0.4570457994644658;
    const float SH_C3_5 = 1.445305721320277;
    const float SH_C3_6 = -0.5900435899266435;
#endif

vec3 evalSH(in vec3 sh[SH_COEFFS], in vec3 dir) {
    float x = dir.x;
    float y = dir.y;
    float z = dir.z;
    vec3 result = SH_C1 * (-sh[0] * y + sh[1] * z - sh[2] * x);
    #if SH_BANDS > 1
        float xx = x * x;
        float yy = y * y;
        float zz = z * z;
        float xy = x * y;
        float yz = y * z;
        float xz = x * z;
        result += sh[3] * (SH_C2_0 * xy) + sh[4] * (SH_C2_1 * yz) +
            sh[5] * (SH_C2_2 * (2.0 * zz - xx - yy)) + sh[6] * (SH_C2_3 * xz) +
            sh[7] * (SH_C2_4 * (xx - yy));
    #endif
    #if SH_BANDS > 2
        result += sh[8] * (SH_C3_0 * y * (3.0 * xx - yy)) +
            sh[9] * (SH_C3_1 * xy * z) + sh[10] * (SH_C3_2 * y * (4.0 * zz - xx - yy)) +
            sh[11] * (SH_C3_3 * z * (2.0 * zz - 3.0 * xx - 3.0 * yy)) +
            sh[12] * (SH_C3_4 * x * (4.0 * zz - xx - yy)) +
            sh[13] * (SH_C3_5 * z * (xx - yy)) + sh[14] * (SH_C3_6 * x * (xx - 3.0 * yy));
    #endif
    return result;
}
#endif
`

const gsplatQuatToMat3VS = /* glsl */ `
mat3 quatToMat3(vec4 R) {
    vec4 R2 = R + R;
    float X = R2.x * R.w;
    vec4 Y = R2.y * R;
    vec4 Z = R2.z * R;
    float W = R2.w * R.w;
    return mat3(
        1.0 - Z.z - W, Y.z + X, Y.w - Z.x,
        Y.z - X, 1.0 - Y.y - W, Z.w + Y.x,
        Y.w + Z.x, Z.w - Y.x, 1.0 - Y.y - Z.z
    );
}

vec4 quatMul(vec4 a, vec4 b) {
    return vec4(
        a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
        a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
        a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
        a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z
    );
}
`

const gsplatModifyVS = /* glsl */ `
void modifySplatCenter(inout vec3 center) {}
void modifySplatRotationScale(
    vec3 originalCenter,
    vec3 modifiedCenter,
    inout vec4 rotation,
    inout vec3 scale
) {}
void modifySplatColor(vec3 center, inout vec4 color) {}
`

const gsplatCopyInstancedQuadVS = /* glsl */ `
in vec3 position;

precision highp usampler2D;
uniform usampler2D uSubDrawData;
uniform ivec2 uTextureSize;
uniform int uSubDrawBase;

flat out ivec4 vSubDraw;

void main(void) {
    int subDrawWidth = textureSize(uSubDrawData, 0).x;
    int idx = gl_InstanceID + uSubDrawBase;
    uvec4 data = texelFetch(uSubDrawData, ivec2(idx % subDrawWidth, idx / subDrawWidth), 0);
    int rowStart = int(data.r & 0xFFFFu);
    int numRows = int(data.r >> 16u);
    int colStart = int(data.g);
    int colEnd = int(data.b);
    int sourceBase = int(data.a);

    float u = float(gl_VertexID & 1);
    float v = float(gl_VertexID >> 1);
    vec4 ndc = vec4(colStart, colEnd, rowStart, rowStart + numRows) /
        vec4(uTextureSize.x, uTextureSize.x, uTextureSize.y, uTextureSize.y) * 2.0 - 1.0;

    gl_Position = vec4(mix(ndc.x, ndc.y, u), mix(ndc.z, ndc.w, v), 0.5, 1.0);
    vSubDraw = ivec4(sourceBase, colStart, colEnd - colStart, rowStart);
}
`

const gsplatWorkBufferGeometryPS = /* glsl */ `
#ifdef GSPLAT_WORKBUFFER_GEOMETRY
    uniform highp usampler2D uWorkBufferTransformA;
    uniform highp usampler2D uWorkBufferTransformB;
    uniform mat4 matrix_model_inverse;
    uniform vec3 uCameraPosition;

    ivec2 wbCoord;
    uvec4 wbTransformA;

    void initWorkBufferGeometry(ivec2 coord) {
        wbCoord = coord;
        wbTransformA = texelFetch(uWorkBufferTransformA, coord, 0);
    }

    vec3 workBufferWorldCenter() {
        return vec3(uintBitsToFloat(wbTransformA.x), uintBitsToFloat(wbTransformA.y), uintBitsToFloat(wbTransformA.z));
    }

    vec4 workBufferWorldRotation() {
        #ifdef GSPLAT_WORKBUFFER_COMPACT
            uint data = texelFetch(uWorkBufferTransformB, wbCoord, 0).x;
            vec3 p = vec3(
                float(data & 0x7FFu) / 2047.0 * 2.0 - 1.0,
                float((data >> 11u) & 0x7FFu) / 2047.0 * 2.0 - 1.0,
                float((data >> 22u) & 0x3FFu) / 1023.0 * 2.0 - 1.0
            );
            float d = dot(p, p);
            return vec4(sqrt(max(0.0, 2.0 - d)) * p, 1.0 - d);
        #else
            vec2 rotXY = unpackHalf2x16(wbTransformA.w);
            vec3 r = vec3(rotXY, unpackHalf2x16(texelFetch(uWorkBufferTransformB, wbCoord, 0).x).x);
            return vec4(r, sqrt(max(0.0, 1.0 - dot(r, r))));
        #endif
    }

    vec3 workBufferWorldScale() {
        #ifdef GSPLAT_WORKBUFFER_COMPACT
            uint data = wbTransformA.w;
            float sx = float(data & 0xFFu);
            float sy = float((data >> 8u) & 0xFFu);
            float sz = float((data >> 16u) & 0xFFu);
            const float logRange = 21.0 / 255.0;
            const float logMin = -12.0;
            return vec3(
                sx == 0.0 ? 0.0 : exp(sx * logRange + logMin),
                sy == 0.0 ? 0.0 : exp(sy * logRange + logMin),
                sz == 0.0 ? 0.0 : exp(sz * logRange + logMin)
            );
        #else
            uvec2 b = texelFetch(uWorkBufferTransformB, wbCoord, 0).xy;
            return vec3(unpackHalf2x16(b.x).y, unpackHalf2x16(b.y));
        #endif
    }

    vec3 quatRotateInv(vec4 q, vec3 v) {
        vec3 t = -q.xyz;
        return v + 2.0 * cross(t, cross(t, v) + q.w * v);
    }

    vec3 getCenter() {
        return (matrix_model_inverse * vec4(workBufferWorldCenter(), 1.0)).xyz;
    }

    vec4 getRotation() {
        vec4 worldRotation = workBufferWorldRotation();
        vec4 localRotation = quatMul(vec4(-model_rotation.xyz, model_rotation.w), worldRotation);
        return localRotation.wxyz;
    }

    vec3 getScale() {
        return workBufferWorldScale() / model_scale;
    }
#endif
`

const gsplatCopyToWorkbufferPS = /* glsl */ `
#define GSPLAT_CENTER_NOPROJ

uniform vec3 model_scale;
uniform vec4 model_rotation;

#include "gsplatHelpersVS"
#include "gsplatFormatVS"
#include "gsplatStructsVS"
#include "gsplatDeclarationsVS"
#include "gsplatCenterVS"
#include "gsplatEvalSHVS"
#include "gsplatQuatToMat3VS"
#include "gsplatReadVS"
#include "gsplatWorkBufferGeometryPS"
#include "gsplatWorkBufferOutputVS"
#include "gsplatWriteVS"
#include "gsplatModifyVS"

flat in ivec4 vSubDraw;
uniform vec3 uColorMultiply;
uniform int uScalarFilterMode;
uniform vec2 uScalarFilterRange;
uniform bool uScalarFilterExempt;

#ifdef GSPLAT_ID
    uniform uint uId;
#endif

bool passScalarFilter(vec3 scale, vec4 baseColor) {
    if (uScalarFilterMode == 0) return true;
    float scalar;
    if (uScalarFilterMode == 1) {
        scalar = scale.x * scale.y * scale.z;
    } else if (uScalarFilterMode == 2) {
        scalar = dot(scale, scale);
    } else {
        scalar = dot(baseColor.rgb, vec3(0.299, 0.587, 0.114));
    }
    return scalar >= uScalarFilterRange.x && scalar <= uScalarFilterRange.y;
}

void main(void) {
    int localRow = int(gl_FragCoord.y) - vSubDraw.w;
    int localCol = int(gl_FragCoord.x) - vSubDraw.y;
    uint originalIndex = uint(vSubDraw.x + localRow * vSubDraw.z + localCol);
    setSplat(originalIndex);

    vec3 worldCenter;
    vec4 worldRotation = vec4(0.0, 0.0, 0.0, 1.0);
    vec3 worldScale = vec3(1.0);
    vec3 sourceScale = vec3(1.0);
    #if SH_BANDS > 0
        vec3 dir;
    #endif

    #ifdef GSPLAT_WORKBUFFER_GEOMETRY
        initWorkBufferGeometry(ivec2(gl_FragCoord.xy));
        worldCenter = workBufferWorldCenter();
        sourceScale = getScale();
        #if SH_BANDS > 0
            dir = normalize(quatRotateInv(model_rotation, worldCenter - uCameraPosition));
        #endif
    #else
        vec3 modelCenter = getCenter();
        worldCenter = (matrix_model * vec4(modelCenter, 1.0)).xyz;
        SplatCenter center;
        initCenter(modelCenter, center);
        vec4 srcRotation = getRotation().yzwx;
        vec3 srcScale = getScale();
        sourceScale = srcScale;
        worldRotation = quatMul(model_rotation, srcRotation);
        if (worldRotation.w < 0.0) worldRotation = -worldRotation;
        worldScale = model_scale * srcScale;
        vec3 originalCenter = worldCenter;
        modifySplatCenter(worldCenter);
        modifySplatRotationScale(originalCenter, worldCenter, worldRotation, worldScale);
        #if SH_BANDS > 0
            dir = normalize(center.view * mat3(center.modelView));
        #endif
    #endif

    vec4 baseColor = getColor();
    vec4 color = baseColor;
    #if SH_BANDS > 0
        vec3 sh[SH_COEFFS];
        float scale;
        readSHData(sh, scale);
        color.xyz += evalSH(sh, dir) * scale;
    #endif
    modifySplatColor(worldCenter, color);
    if (!uScalarFilterExempt && !passScalarFilter(sourceScale, baseColor)) color.a = 0.0;
    color.xyz *= uColorMultiply;
    writeSplat(worldCenter, worldRotation, worldScale, color);

    #ifdef GSPLAT_ID
        writePcId(uvec4(uId, 0u, 0u, 0u));
    #endif
}
`

export const WEBGL_GSPLAT_WORK_BUFFER_CHUNKS = Object.freeze({
  gsplatContainerDeclVS: '#include "gsplatContainerDeclarationsVS"',
  gsplatCenterVS,
  gsplatCopyInstancedQuadVS,
  gsplatCopyToWorkbufferPS,
  gsplatEvalSHVS,
  gsplatFormatVS,
  gsplatHelpersVS,
  gsplatModifyVS,
  gsplatQuatToMat3VS,
  gsplatSplatVS,
  gsplatStructsVS,
  gsplatWorkBufferGeometryPS,
})
